import React, { useEffect, useRef, useState } from 'react';

import './App.css';
import Login from './components/Login';
import AuthContentWrapper from './components/AuthContentWrapper';
import RegisterStrava from './components/RegisterStrava';
import * as api from './api/api';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";

function AuthenticatedEntry({ isAuthenticated, setIsAuthenticated, initialView }) {
  return (
    <div>
      <Login setIsAuthenticated={setIsAuthenticated} />
      {isAuthenticated ? (
        // key={initialView} forces a clean remount whenever the view
        // category actually changes (e.g. Graph -> Settings), rather than
        // reusing the same AuthContentWrapper instance and relying on it to
        // notice the prop changed. Without this, a view like ActivityGraph
        // (which writes its own default filters back into the URL) can
        // still be mounted for one extra render after the URL has already
        // changed to the new page, see that now-current location, and
        // rewrite its own params onto it.
        <AuthContentWrapper key={initialView} initialView={initialView} />
      ) : null}
    </div>
  );
}

// On first landing at the root URL once signed in, send the user straight to
// wherever is actually useful rather than the generic overview: Settings (to
// connect Strava - the Connections box there is the only place to do that)
// if nothing is connected yet, then add a goal if there's nothing to plan
// against, otherwise straight to the plan. Rendered alongside <Routes> (not
// inside it) so it stays mounted across in-app navigation — the
// hasCheckedRef guard then means the check only runs once per page load; a
// later visit to "/" goes to the overview. "/" itself renders no page (see
// the LANDING view) so nothing - the overview and its scroll-to-year
// included - flashes up before the redirect lands, and replace keeps "/"
// out of history so Back doesn't bounce through it again.
// Browsers restore the previous scroll position on back/forward but not on
// a plain navigate() to a new path, and nothing else here resets it - a
// switch to a new tab (or a deep view like plan/week/:date) would otherwise
// keep whatever scroll position the previous page was left at.
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function OnboardingRedirect({ isAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || location.pathname !== '/') {
      return;
    }
    if (hasCheckedRef.current) {
      navigate('/goals', { replace: true });
      return;
    }
    hasCheckedRef.current = true;
    Promise.all([api.getStravaConnection(), api.getGoals()])
      .then(([connection, goalsResponse]) => {
        if (!connection?.connected) {
          // Settings itself scrolls to the Connections panel whenever
          // there's no connection yet, so landing here needs no extra
          // signal - it covers both this redirect and a direct visit.
          navigate('/settings', { replace: true });
        } else if (!goalsResponse?.data?.length) {
          navigate('/edit', { replace: true });
        } else {
          navigate('/plan', { replace: true });
        }
      })
      .catch(() => {
        // Unable to determine onboarding state - fall back to the default
        // overview rather than guessing where to send them.
        navigate('/goals', { replace: true });
      });
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("ServiceWorker.js").then(registration => {
          console.log("SW Registered!");
      }).catch(error => {
          console.log("SW Registration Failed");
      });
  } else {
      console.log("Not supported");
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <OnboardingRedirect isAuthenticated={isAuthenticated} />
      <Routes>
        <Route path="/" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="LANDING" />} />
        <Route path="goals" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="MAIN" />} />
        <Route path="graph" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="GRAPH" />} />
        <Route path="activity" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="ACTIVITY" />} />
        <Route path="activity/:activityId" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="ACTIVITY" />} />
        <Route path="edit" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="GOALS" />} />
        <Route path="plan" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="PLAN" />} />
        <Route path="plan/week/:weekStartDate" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="PLAN" />} />
        <Route path="plan/year/:year" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="PLAN" />} />
        <Route path="settings" element={<AuthenticatedEntry isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} initialView="SETTINGS" />} />
          <Route path="register/strava" element={<RegisterStrava />} />
          <Route path="*" element={<div> No Page </div>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
